import type { JSX, ReactNode } from "react";
import { useMemo, useState } from "react";
import { useAnalyticsApp } from "@/features/analytics/context";
import type {
  AnalyticsCategory,
  AnalyticsFieldDescriptor,
} from "@/features/analytics/filterCategories";
import { DictMultiSelectField } from "./DictMultiSelectField";
import { AsyncLookupMultiSelectField } from "./AsyncLookupMultiSelectField";
import { MultiSelectField, TextListField } from "./fields";
import type { ChoiceOption } from "./types";
import { asArray, coerceId, normalizeOp } from "./analyticsConditionUtils";

// Analytics-aware CategoryCard body. Renders each field of an analytics category
// (from filterCategories.buildAnalyticsCategories) directly from its render
// descriptor — WITHOUT going through SEGMENT_FIELDS/getFieldOptionConfig, so the
// analytics field vocabulary (channel/provider/campaign/… none of which are
// segment fields) works. The segment rendering path is untouched: CategoryCard
// only reaches this body when a category carries `fieldDescriptors`.
//
// Each field emits ONE condition { field, op: "in"|"nin", value: [ids] }. The
// combined drawer value is a SegmentDefinition { all: [...] } assembled by
// BuildConditions from these conditions (Include-only / AND-locked in analytics).

export type Condition = {
  id?: string;
  field: string;
  op: string;
  value: unknown;
  disabled?: boolean;
};

const uid = (): string =>
  crypto.randomUUID?.() ??
  `c-${Date.now().toString()}-${Math.random().toString(16).slice(2)}`;

const OpToggle = ({
  op,
  onChange,
}: {
  op: "in" | "nin";
  onChange: (next: "in" | "nin") => void;
}): JSX.Element => (
  <div className="inline-flex overflow-hidden rounded-(--radius-md) border border-(--border-subtle)">
    {(
      [
        ["in", "is any of"],
        ["nin", "is none of"],
      ] as const
    ).map(([value, label]) => (
      <button
        key={value}
        type="button"
        onClick={() => onChange(value)}
        className={`px-2.5 py-1 text-[12px] transition-colors ${
          op === value
            ? "bg-(--pastel-violet) text-(--brand-primary)"
            : "text-(--text-secondary) hover:bg-(--surface-2)"
        }`}
      >
        {label}
      </button>
    ))}
  </div>
);

// `title` replaces the field label in the header; `trailing` sits after the
// operator toggle (the Filters section's remove button).
export const FieldRow = ({
  descriptor,
  condition,
  onChange,
  title,
  trailing,
}: {
  descriptor: AnalyticsFieldDescriptor;
  condition: Condition | undefined;
  onChange: (next: Condition) => void;
  title?: ReactNode;
  trailing?: ReactNode;
}): JSX.Element => {
  const { availableChannels, availableCategories } = useAnalyticsApp();
  // Label cache for async (dict/lookup) controls so freshly-selected chips keep
  // their names within the session (ids alone would show as raw uuids/ints).
  const [labels, setLabels] = useState<Record<string, string>>({});

  const op = normalizeOp(condition?.op);
  const rawValues = asArray(condition?.value);

  const emit = (
    nextValues: (string | number)[],
    nextOp: "in" | "nin",
  ): void => {
    onChange({
      id: condition?.id ?? uid(),
      field: descriptor.key,
      op: nextOp,
      value: nextValues,
      disabled: false,
    });
  };

  const control = descriptor.control;
  const placeholder = `Search ${descriptor.label.toLowerCase()}…`;

  // Merge freshly-selected async {value,label} into the session label cache so
  // chips keep their names (raw ids alone would show).
  const cacheLabels = (
    opts: { value: string | number; label: string }[],
  ): void =>
    setLabels((prev) => {
      const merged = { ...prev };
      for (const o of opts) merged[String(o.value)] = o.label;
      return merged;
    });

  const renderControl = (): JSX.Element => {
    switch (control.kind) {
      case "enum": {
        const selected = rawValues.map((v) => coerceId(v, control.valueType));
        return (
          <MultiSelectField
            label={descriptor.label}
            options={control.options}
            value={selected}
            onChange={(next) => emit(next, op)}
            placeholder={placeholder}
          />
        );
      }
      case "context": {
        // channels: int id; categories: string id.
        const options: ChoiceOption<string | number>[] =
          control.source === "channels"
            ? availableChannels.map((ch) => ({ value: ch.id, label: ch.name }))
            : availableCategories.map((cat) => ({
                value: cat.id,
                label: cat.name,
              }));
        const selected = rawValues.map((v) =>
          coerceId(v, control.source === "channels" ? "int" : "text"),
        );
        return (
          <MultiSelectField
            label={descriptor.label}
            options={options}
            value={selected}
            onChange={(next) => emit(next, op)}
            placeholder={placeholder}
          />
        );
      }
      case "dict": {
        // dict ids are ints; DictMultiSelectField works in ChoiceOption<number>.
        const selected: ChoiceOption<number>[] = rawValues.map((v) => {
          const id = Number(v);
          return { value: id, label: labels[String(id)] ?? String(id) };
        });
        return (
          <DictMultiSelectField
            label={descriptor.label}
            dictType={control.dictType}
            value={selected}
            onChange={(next) => {
              cacheLabels(next);
              emit(
                next.map((o) => o.value),
                op,
              );
            }}
            placeholder={placeholder}
          />
        );
      }
      case "text":
        return (
          <TextListField
            label={descriptor.label}
            value={rawValues.map(String)}
            onChange={(next) => emit(next, op)}
            normalize={control.normalize}
          />
        );
      case "lookup": {
        // lookup ids are uuid strings.
        const selected: ChoiceOption<string>[] = rawValues.map((v) => {
          const id = String(v);
          return { value: id, label: labels[id] ?? id };
        });
        return (
          <AsyncLookupMultiSelectField
            label={descriptor.label}
            lookup={control.lookup}
            value={selected}
            onChange={(next) => {
              cacheLabels(next);
              emit(
                next.map((o) => o.value),
                op,
              );
            }}
            placeholder={placeholder}
          />
        );
      }
    }
  };

  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex items-center justify-between gap-2">
        <span className="text-[12px] font-semibold text-(--text-secondary)">
          {title ?? descriptor.label}
        </span>
        <div className="flex items-center gap-1.5">
          <OpToggle
            op={op}
            onChange={(next) => emit(rawValues as (string | number)[], next)}
          />
          {trailing}
        </div>
      </div>
      {/* Each control renders its own label; hide the duplicate via the wrapper. */}
      <div className="[&_label>span:first-child]:sr-only">
        {renderControl()}
      </div>
    </div>
  );
};

export const AnalyticsCategoryBody = ({
  category,
  conditions,
  onSetConditions,
}: {
  category: AnalyticsCategory;
  conditions: Condition[];
  onSetConditions: (next: Condition[]) => void;
}): JSX.Element => {
  const byField = useMemo(() => {
    const map: Record<string, Condition> = {};
    for (const c of conditions) map[c.field] = c;
    return map;
  }, [conditions]);

  const upsert = (next: Condition): void => {
    const others = conditions.filter((c) => c.field !== next.field);
    // Keep the condition even when its value is empty: the category card's
    // presence in BuildConditions is condition-derived, so dropping empties
    // would make the whole card disappear the moment a field is cleared. Empty
    // (`value: []`) conditions are a safe no-op on the backend — both
    // segmentFiltersToSql and the analytics response builder skip zero-value
    // conditions (filtersToSql.ts:167, analyticsFilter.ts). Removing the card is
    // done via the card's own remove button (BuildConditions.removeCategory).
    onSetConditions([...others, next]);
  };

  return (
    <div className="flex flex-col gap-3">
      {category.fields.map((key) => {
        const descriptor = category.fieldDescriptors[key];
        if (!descriptor) return null;
        return (
          <FieldRow
            key={key}
            descriptor={descriptor}
            condition={byField[key]}
            onChange={upsert}
          />
        );
      })}
    </div>
  );
};
